<template>
  <div>
    <div class="row mb-5">
      <div class="col-8">
        <label>{{$t('CONTENT.payments.lessor')}}:<span class="b-form__required"> *</span></label>
        <cool-select :list="assetLessors"
                     option-value="id"
                     option-text="value"
                     :placeholder="$t('CONTENT.choose')"
                     :is-disabled="selectLessorDisabled"
                     :isError="isFieldWithError('lessorComponentShareId', payment.lessorComponentShareId)"
                     v-model="payment.lessorComponentShareId"/>
      </div>
    </div>
    <div class="row mb-5">
      <div class="col-12 b-form__container_type_flex">
        <div class="col-11 px-0 b-form__container_type_flex b-form__row_type_flex-end">
          <div class="col-8 pl-0">
            <label>{{$t('CONTENT.payments.payment_name_creation')}}:<span class="b-form__required"> *</span></label>
            <cool-select :list="getPaymentNames"
                         option-value="idInDB"
                         option-text="name"
                         :placeholder="$t('CONTENT.choose')"
                         :isError="isFieldWithError('paymentNameId', payment.paymentNameId)"
                         v-model="payment.paymentNameId"/>
          </div>
          <div class="px-0 col-4">
            <div class="b-form__container">
              <label for="externalId">{{$t('CONTENT.external_id')}}:</label>
              <input
                data-cy="externalId"
                type="text"
                id="externalId"
                class="b-input"
                name="externalId"
                v-model="payment.externalId"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 mt-5" v-if="payment.paymentNameId">
        <div class="row">
          <div class="col-4">
            <div class="b-form__container">
              <label>{{$t('CONTENT.payments.payment_type_creation')}}:</label>
              {{paymentType}}
            </div>
          </div>
          <div class="col-4" v-if="paymentKind !== null">
            <div class="b-form__container">
              <label>{{$t('CONTENT.payments.payment_kind_creation')}}:</label>
              {{paymentKind}}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="b-contract__section-inner pt-0">
      <div class="row">
        <div class="col-3">
          <div>
            <label>{{$t('CONTENT.payments.payment_currency_code')}}:<span class="b-form__required"> *</span></label>
            <cool-select :list="allCurrencyCode"
                         option-value="code"
                         option-text="name"
                         :placeholder="$t('CONTENT.choose')"
                         :isError="isFieldWithError('currencyCode', currencyCode)"
                         v-model="currencyCode"></cool-select>
          </div>
        </div>
      </div>
      <div class="row mt-5" v-show="currencyCode && currencyCode !== getContractSettings.currencyCode.toLowerCase()">
        <CurrencyRateParametersWidgetWithoutStore
          :currencyCode="currencyCode"
          :currencyRateParameters="payment.currencyRateParameters"
          :date="assetComponent.startDate"
          :validationErrors="validationErrors"
          :payment="payment"
          :assetStartDate="payment.startDate"
          :assetEndDate="payment.endDate"
          @currencyRateDataChange="setCurrencyRate"
        />
      </div>
      <div class="mt-5">
        <h4>{{$t('CONTENT.discountRates.rate')}}</h4>
        <div class="row mt-2 b-form__container_type_flex b-form__row_type_flex-end">
          <div class="col-5" data-cy="payment-lease-rate">
            <label>{{$t('CONTENT.payments.payment_lease_rate')}}:<span class="b-form__required"> *</span></label>
            <numeric v-bind:class="{ 'b-numeric__error': isFieldWithError('leaseRate', payment.leaseRate) }" class="b-numeric" controls :min="0" :max="1000000000000" v-model="payment.leaseRate"></numeric>
          </div>
          <div class="col-3">
            <label>{{$t('CONTENT.payments.payment_units')}}:<span class="b-form__required"> *</span></label>
            <input
              type="text"
              id="paymentUnitOfMeasure"
              class="b-input"
              name="paymentUnitOfMeasure"
              disabled
              v-model="unitOfMeasure"
            />
          </div>
          <div class="col-4">
            <label>{{$t('CONTENT.payments.payment_lease_frequency')}}:<span class="b-form__required"> *</span></label>
            <cool-select :list="ratePeriods"
                         option-value="value"
                         option-text="text"
                         :placeholder="$t('CONTENT.choose')"
                         :isError="isFieldWithError('leaseFrequency', payment.leaseFrequency)"
                         v-model="payment.leaseFrequency"
                         :is-disabled="paymentFrequencyOnlyOnce()"
            />
          </div>
        </div>
      </div>
      <div class="row mt-5">
        <div class="col-5 mb-2 b-form__container_type_flex">
          <checkbox class="b-checkbox  b-from__checkbox_pointer-event_none" id="isSimplified"
                    v-model="isSimplified">{{$t('CONTENT.payments.is_simplified')}}
          </checkbox>
          <!--          <dynamic-icon class="ml-1" v-tooltip="$t('CONTENT.hints.default_hint')" icon="question"></dynamic-icon>-->
        </div>
      </div>
      <div class="row mt-5 b-form__container_type_flex b-form__row_type_flex-end">
        <div class="col-3">
          <div>
            <div class="b-form__container_type_flex">
              <label>{{$t('CONTENT.payments.payment_date')}}:<span class="b-form__required"> *</span></label>
              <dynamic-icon class="ml-1 mb-2" v-tooltip="$t('CONTENT.hints.paymentDateHint')" icon="question"></dynamic-icon>
            </div>
            <datepicker v-bind:class="{ 'b-datepicker__error': isFieldWithError('startDate', startDate) }"
                        class="b-datepicker"
                        v-model="startDate"
                        :formatter="datepickerFormat"
                        :disabled-date="maxDateForStartDateFn"/>
          </div>
        </div>
        <div class="col-3">
          <div>
            <datepicker :class="{ 'b-datepicker__error': isFieldWithError('endDate', endDate) }"
                        class="b-datepicker" v-model="endDate" :formatter="datepickerFormat"
                        :disabled-date="minDateForEndDateFn"></datepicker>
          </div>
        </div>
        <div class="col-3">
          <div>
            <label>{{$t('CONTENT.payments.payment_first_date')}}:<span class="b-form__required"> *</span></label>
            <datepicker v-bind:class="{ 'b-datepicker__error': isFieldWithError('firstPaymentDate', firstPaymentDate) }"
                        class="b-datepicker" v-model="firstPaymentDate" :formatter="datepickerFormat"
                        :disabled="payment.isLastPeriodDate"></datepicker>
          </div>
        </div>
        <div class="col-3">
          <label>{{$t('CONTENT.payments.payment_frequency')}}:<span class="b-form__required"> *</span></label>
          <cool-select :list="periods"
                       option-value="value"
                       option-text="text"
                       :placeholder="$t('CONTENT.choose')"
                       :isError="isFieldWithError('paymentFrequency', payment.paymentFrequency)"
                       v-model="payment.paymentFrequency"
                       :is-disabled="paymentFrequencyOnlyOnce()"
          />
        </div>
        <div class="col-3">
          <checkbox class="b-checkbox" v-model="isLastPeriodDate">{{$t('CONTENT.payments.is_last_period_date')}}</checkbox>
        </div>
      </div>
      <IndexationParametersWidgetWithoutStore
        :payment="payment"
        :paymentsName="paymentsName"
        :validationErrors="validationErrors"/>
      <DiscountParametersWidgetWithoutStore
        :assetComponent="assetComponent"
        :payment="payment"
        :validationErrors="validationErrors"/>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import dateConverter from "../../../utils/converters/date.converter";
import datepickerFormat from '@/mixins/datepickerFormat.mixin';

export default {
  props: {
    lessorComponentShare: {
      type: Object
    },
    payment: {
      type: Object
    },
    unitOfMeasure: {
      type: String
    },
    assetComponent: {
      type: Object
    },
    validationErrors: {
      type: Array
    }
  },
  mixins: [datepickerFormat],
  data() {
    return {
      paymentType: null,
      paymentKind: null,
      isSimplified: true,
      maxDateForStartDate: null,
      minDateForEndDate: null,
      selectLessorDisabled: false
    }
  },
  computed: {
    ...mapGetters(["getPaymentNames"]),
    ...mapGetters(["getCurrencyCode"]),
    ...mapGetters(["getFrequencies"]),
    ...mapGetters(["getLeaseFrequencies"]),
    ...mapGetters(["getContractSettings"]),
    ...mapGetters(["getContractEventState"]),
    ...mapGetters(["getCounterpartiresForContract"]),
    assetLessors() {
      return this.assetComponent.lessorComponentShares
        .map(lessorComponentShare => {
          const lessor = this.getCounterpartiresForContract
            .find(counterparty => counterparty.id === lessorComponentShare.lessorId)
          return {
            id: lessorComponentShare.idInDB,
            value: lessor.value
          }
        }).filter(counterparty => counterparty !== undefined)
    },
    isContractEventState() {
      return this.getContractEventState;
    },
    periods() {
      return this.getFrequencies;
    },
    ratePeriods() {
      return this.getLeaseFrequencies;
    },
    allCurrencyCode() {
      return this.getCurrencyCode;
    },
    paymentsName() {
      return this.getPaymentNames;
    },
    currencyCode: {
      get() {
        if(this.payment.currencyCode != null) {
          return this.payment.currencyCode.toLowerCase();
        } else {
          return null;
        }
      },
      set(newVal) {
        this.payment.currencyCode = newVal.toUpperCase();
      }
    },
    isAddCurrencyCode() {
      if (this.payment.currencyCode !== null) {
        return true;
      }
    },
    startDate: {
      get() {
        if (this.payment.startDate === null && this.isContractEventState) {
          this.payment.startDate = this.lessorComponentShare.startDate;
          return dateConverter.convertToDate(this.payment.startDate);
        } else if (this.payment.startDate === null && this.assetComponent.startDate !== null) {
          this.payment.startDate = this.assetComponent.startDate;
          return dateConverter.convertToDate(this.payment.startDate);
        } else {
          return dateConverter.convertToDate(this.payment.startDate);
        }
      },
      set(newVal) {
        if(newVal !== null) {
          let startDateObject = newVal;
          this.minDateForEndDate = newVal;
          this.minDateForEndDateFn();
          this.payment.startDate = dateConverter.convertToString(startDateObject);
        } else {
          this.payment.startDate = null;
        }
      }
    },
    endDate: {
      get() {
        if (this.payment.endDate === null && this.assetComponent.endDate !== null) {
          this.payment.endDate = this.assetComponent.endDate;
          return dateConverter.convertToDate(this.payment.endDate);
        } else {
          return dateConverter.convertToDate(this.payment.endDate);
        }
      },
      set(newVal) {
        if(newVal !== null) {
          let endDateObject = newVal;
          this.maxDateForStartDate = newVal;
          this.maxDateForStartDateFn();
          this.payment.endDate = dateConverter.convertToString(endDateObject);
        } else {
          this.payment.endDate = null;
        }
      }
    },
    firstPaymentDate: {
      get() {
        if (this.payment.firstPaymentDate === null) {
          return null;
        } else {
          return dateConverter.convertToDate(this.payment.firstPaymentDate);
        }
      },
      set(newVal) {
        if(newVal !== null) {
          this.payment.firstPaymentDate = dateConverter.convertToString(newVal);
        } else {
          this.payment.firstPaymentDate = null;
        }
      }
    },
    isLastPeriodDate: {
      get() {
        return this.payment.isLastPeriodDate;
      },
      set(newVal) {
        this.payment.isLastPeriodDate = newVal;

        if(this.payment.isLastPeriodDate) {
          let startDate = dateConverter.convertToDate(this.payment.startDate);

          if (this.payment.firstPaymentDate !== null)
            startDate = dateConverter.convertToDate(this.payment.firstPaymentDate);

          const lastDayOfMonth = new Date(startDate.getFullYear(), startDate.getMonth()+1, 0);
          this.payment.firstPaymentDate = dateConverter.convertToString(lastDayOfMonth);
        }
      }
    },
  },
  watch: {
    'payment.paymentNameId': {
      handler: 'updatePaymentInfo',
      immediate: true
    }
  },
  created() {
    let lessorComponentShare = this.assetComponent.lessorComponentShares
      .find(l => l.paymentsParameters.find(payment => payment.idInDB === this.payment.idInDB) !== undefined)
    if(lessorComponentShare === undefined) lessorComponentShare = this.lessorComponentShare

    if(lessorComponentShare !== undefined) {
      this.payment.lessorComponentShareId = lessorComponentShare.idInDB
      this.selectLessorDisabled = true
    }

    if (this.isContractEventState) {
      let discount = null;
      this.assetComponent.lessorComponentShares.forEach((lessor) => {
        lessor.paymentsParameters.forEach((payment) => {
          if (payment.discount.discountRate) {
            discount = payment.discount.discountRate;
          };
        });
      });
      this.payment.discount.discountRate = discount;
    }
  },
  methods: {
    updatePaymentInfo() {
      const neededPayment = this.getPaymentNames.find(p => p.idInDB === this.payment.paymentNameId);

      if (neededPayment) {
        this.paymentType = this.$t("CONTENT.payments." + neededPayment.paymentTypeCode);
        if(neededPayment.paymentKindCode !== null) {
          this.paymentKind = this.$t("CONTENT.payments." + neededPayment.paymentKindCode);
        } else this.paymentKind = null
      }

      if (this.paymentFrequencyOnlyOnce()) {
        this.payment.leaseFrequency = 'Once'
        this.payment.paymentFrequency = 'Once'
      }
    },
    paymentFrequencyOnlyOnce() {
      const payment = this.getPaymentNames.find(p => p.idInDB === this.payment.paymentNameId)
      if (payment === undefined) return false
      if (payment.paymentTypeCode === 'fixed_lease_payment' && payment.paymentKindCode === 'upfront_payment') return true
      if (payment.paymentTypeCode === 'not_lease_payment' && payment.paymentKindCode === 'guarantee_payment_returnable') return true
      if (payment.paymentTypeCode === 'residual_value_guarantees' && payment.paymentKindCode === null) return true
      if (payment.paymentTypeCode === 'variable_lease_payment_depend_on_index' && payment.paymentKindCode === 'guarantee_payment_accountable') return true
      if (payment.paymentTypeCode === 'purchase_option' && payment.paymentKindCode === null) return true
      if (payment.paymentTypeCode === 'penalty_payment' && payment.paymentKindCode === null) return true
      return false
    },
    maxDateForStartDateFn(date) {
      if(this.isContractEventState) {
        let minstartDate = this.lessorComponentShare.startDate
        return date < dateConverter.convertToDate(minstartDate)
      }

      if(this.maxDateForStartDate !== null) {
        return date > this.maxDateForStartDate;
      }
    },
    minDateForEndDateFn(date) {
      if(this.minDateForEndDate !== null) {
        return date < this.minDateForEndDate;
      }
    },
    setCurrencyRate(data) {
      this.payment.currencyRateParameters = data;
    },
    isFieldWithError(field, value) {
      return !value && this.validationErrors && this.validationErrors.includes(field);
    }
  }
}
</script>
